import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [validState, setValidState] = useState({ valid: false, error: '' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setValidState({ valid: false, error: 'Missing reset token.' });
        setValidating(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/reset-password/validate`, { params: { token } });
        setValidState({ valid: true, error: '' });
      } catch (err) {
        setValidState({ valid: false, error: err.response?.data?.detail || 'Invalid or expired token.' });
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password updated');
      setTimeout(() => navigate('/admin/login'), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-gray-200 shadow-xl" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl text-[#193460]">Reset Password</CardTitle>
          <CardDescription>Choose a new password for your account.</CardDescription>
        </CardHeader>
        <CardContent>
          {validating ? (
            <p className="text-center text-gray-500 py-8">Validating link...</p>
          ) : !validState.valid ? (
            <div className="text-center py-6 space-y-4">
              <AlertCircle className="w-14 h-14 text-red-500 mx-auto" />
              <p className="text-gray-700">{validState.error}</p>
              <Link to="/admin/forgot-password">
                <Button className="bg-[#E87B6A] hover:bg-[#d66a59] text-white" data-testid="request-new-link-btn">
                  Request a new link
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-6 space-y-4">
              <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
              <p className="text-gray-700">Your password has been updated. Redirecting to login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="new-password" className="flex items-center text-[#193460] font-semibold mb-2">
                  <Lock className="w-4 h-4 mr-2" /> New Password
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  className="h-12 border-2"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="flex items-center text-[#193460] font-semibold mb-2">
                  <Lock className="w-4 h-4 mr-2" /> Confirm Password
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                  minLength={8}
                  className="h-12 border-2"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                size="lg"
                data-testid="reset-submit-btn"
                className="w-full bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold h-14 text-lg"
              >
                {submitting ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
